// CitationPopover — the panel CONTENT shown when a Citation marker is opened. Presentational:
// given a resolved CitationRef (cited chunk + optional source artifact), it renders the cited
// PASSAGE, the source line, and two actions (view full source artifact / view lineage). The
// trigger + positioning are the mount's job (compose with the shell Popover); this is just content
// so it unit-tests in isolation.
import { ExternalLink, GitFork, ShieldAlert } from "lucide-react";

import type { CitationRef } from "./types";

export interface CitationPopoverProps {
  citation: CitationRef;
  /** Max passage characters before an ellipsis (the chunk content can be long). */
  passageLimit?: number;
  onViewSource?: () => void;
  onViewLineage?: () => void;
}

function passage(content: string, limit: number): string {
  const t = content.trim();
  return t.length <= limit ? t : t.slice(0, limit).trimEnd() + "…";
}

export function CitationPopover({
  citation,
  passageLimit = 320,
  onViewSource,
  onViewLineage,
}: CitationPopoverProps): React.ReactElement {
  const { chunk, source } = citation;
  const cited = chunk.role === "cited";
  return (
    <div className="w-72 p-3 text-sm">
      <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]">
        <span>{cited ? "Cited" : "Retrieved"} passage</span>
        <span className="text-[var(--color-text-tertiary)]">· chunk #{chunk.ord}</span>
      </div>
      <blockquote className="border-l-2 border-[var(--color-accent)] pl-2.5 text-[var(--color-text-primary)]">
        {passage(chunk.content, passageLimit)}
      </blockquote>
      <div className="mt-2.5 flex items-center gap-1.5 text-xs text-[var(--color-text-secondary)]">
        {source ? (
          <>
            <span className="truncate text-[var(--color-text-primary)]" title={source.filename}>
              {source.filename}
            </span>
            <span className="rounded-[2px] bg-[var(--color-surface-muted)] px-1 py-px text-[10px]">{source.zone}</span>
            {source.quarantined && (
              <span className="inline-flex items-center gap-0.5 text-[var(--color-danger-500)]">
                <ShieldAlert size={11} aria-hidden /> quarantined
              </span>
            )}
          </>
        ) : (
          <span className="font-mono text-[11px]">source {chunk.source_id.slice(0, 8)}…</span>
        )}
      </div>
      <div className="mt-2.5 flex items-center gap-3 border-t border-[var(--color-border-subtle)] pt-2">
        <button
          type="button"
          onClick={onViewSource}
          disabled={!onViewSource}
          className="inline-flex items-center gap-1 text-xs text-[var(--color-accent)] hover:underline disabled:text-[var(--color-text-tertiary)] disabled:no-underline"
        >
          <ExternalLink size={12} aria-hidden /> View source
        </button>
        <button
          type="button"
          onClick={onViewLineage}
          disabled={!onViewLineage}
          className="inline-flex items-center gap-1 text-xs text-[var(--color-accent)] hover:underline disabled:text-[var(--color-text-tertiary)] disabled:no-underline"
        >
          <GitFork size={12} aria-hidden /> View lineage
        </button>
      </div>
    </div>
  );
}
